import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PageContainer from '@/components/Container/PageContainer';
import { useSearchParams } from 'react-router-dom';
import useUserDetail from '@/hooks/useUserDetail';
import Introduction from '@/components/ETrust/GoGlobal/Introduction';
import RegistrationForm from '@/components/ETrust/GoGlobal/RegistrationForm';
import RegistrationStatus from '@/components/ETrust/GoGlobal/RegistrationStatus';
import Stepline from '@/components/ETrust/Stepline';
import { useTranslation } from 'react-i18next';
import { LoadingPage } from '@/layout';
import { getGoGlobalApplication } from '@/api/auth.api';
import { createSingleFlightApplicationLoader } from './applicationLoader';

const GoGlobal = () => {
    // @ts-ignore
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState<number>(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState(searchParams.get('step'));
    // 此頁不會進行使用者資料的更新, 故activeStep填固定值避免更新
    useUserDetail({});
    const stepline = t('pages.eTrust.goGlobal.stepline', { returnObjects: true });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [applicationData, setApplicationData] = useState<GoGlobalApplication | null>(null);
    const initialStatusCheckStarted = useRef<boolean>(false);
    const requestedStep = searchParams.get('step');
    const loadApplicationRequest = useMemo(
        () => createSingleFlightApplicationLoader(getGoGlobalApplication),
        []
    );

    const loadApplication = useCallback(async (redirectToStatus = false) => {
        setIsLoading(true);
        try {
            const res = await loadApplicationRequest();
            if (res.data.success && res.data.data) {
                setApplicationData(res.data.data);
                if (redirectToStatus && requestedStep !== '3') {
                    setCurrentStep('3');
                    setActiveStep(2);
                    setSearchParams({ step: '3' }, { replace: true });
                }
            }
        } catch (error) {
            // No application is a valid state for a first-time applicant.
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }, [loadApplicationRequest, requestedStep, setSearchParams]);

    useEffect(() => {
        const normalizedStep = requestedStep === '2' || requestedStep === '3' ? requestedStep : '1';

        setCurrentStep(normalizedStep);
        setActiveStep(Number(normalizedStep) - 1);

        if (requestedStep !== normalizedStep) {
            setSearchParams({ step: normalizedStep }, { replace: true });
        }
    }, [requestedStep, setSearchParams]);

    useEffect(() => {
        if (initialStatusCheckStarted.current) {
            return;
        }

        initialStatusCheckStarted.current = true;
        loadApplication(true);
    }, [loadApplication]);

    return (
        <PageContainer title={t('pages.eTrust.goGlobal.title')}>
            <LoadingPage isLoading={isLoading} />
            <div style={{ display: 'flex' }}>
                <Stepline activeStep={activeStep} stepline={stepline} type={'GoGlobal'} />
                {activeStep === 0 && (
                    <Introduction setActiveStep={setActiveStep} setCurrentStep={setCurrentStep} />
                )}
                {activeStep === 1 && (
                    <RegistrationForm 
                        setActiveStep={setActiveStep} 
                        setCurrentStep={setCurrentStep}
                        refreshStatus={() => {
                            return loadApplication(false);
                        }}
                        applicationData={applicationData}
                    />
                )}
                {activeStep === 2 && applicationData && (
                    <RegistrationStatus 
                        setActiveStep={setActiveStep} 
                        setCurrentStep={setCurrentStep}
                        applicationData={applicationData}
                    />
                )}
            </div>
        </PageContainer>
    );
};

export default GoGlobal;
